<script setup lang="ts">
import {ref} from "vue";
import {CapacityFramework, CapacityFrameworkRole, Filters, Maintainer} from "@/types";
import {DataTableHeader} from "vuetify/framework";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {useAuth} from "@/composables/auth";
import SearchField from "@/components/forms/SearchField.vue";
import SortField from "@/components/forms/SortField.vue";
import IconDateTooltip from "@/components/tooltips/IconDateTooltip.vue";
import MeChip from "@/components/chips/MeChip.vue";
import RoleSelect from "@/Pages/capacity_frameworks/components/RoleSelect.vue";
import {useCapacityFramework} from "@/composables/capacity_framework";
import CapacityFrameworkLayout from "@/layouts/CapacityFrameworkLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import MaintainerActionsBtn from "@/Pages/capacity_frameworks/components/MaintainerActionsBtn.vue";

defineOptions({layout: [BaseLayout, CapacityFrameworkLayout]})

const {user} = useAuth()
const {isAdmin} = useCapacityFramework()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    items: Array<Maintainer>
    filters: Filters
    hasJoined: boolean
}

const props = defineProps<Props>();

const search = ref<string | null>(props.filters.search)
const sortBy = ref<string>(props.filters.sort_by)
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir)

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Account',
        value: 'name'
    },
    {
        title: 'Access granted',
        value: 'joined_at'
    },
    {
        title: 'Role',
        value: 'role'
    },
    {
        title: 'User created',
        value: 'user_created_at'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Account',
        value: 'name',
        sortable: false,
        align: 'start',
    },
    {
        title: 'Role',
        value: 'role',
        sortable: false,
        align: 'start',
    },
    {
        title: 'Activity',
        value: 'activity',
        sortable: false,
        align: 'start',
    },
    {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end',
    },
]

const loadItems = () => {
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    router.get(
        route('capacity-frameworks.users.index', {capacityFramework: props.capacityFramework.id}),
        params,
        {
            preserveState: true,
        }
    );
}

const updateUserRole = (userId: number, newRole: CapacityFrameworkRole) => {
    router.put(
        route('capacity-frameworks.users.update', {capacityFramework: props.capacityFramework.id, user: userId}),
        {
            role: newRole,
        }
    );
}

const removeMaintainer = (userId: number) => {
    router.delete(route('capacity-frameworks.users.destroy', {capacityFramework: props.capacityFramework.id, user: userId}),);
}
</script>

<template>
    <v-row class="mt-3 bg-grey-lighten-5 border-t-sm border-b-sm">
        <v-col
            cols="12"
            md="8"
            class="py-4"
        >
            <search-field
                v-model="search"
                @click="loadItems"
            />
        </v-col>
        <v-col
            cols="12"
            md="4"
            class="py-4"
        >
            <sort-field
                v-model:sort-by="sortBy"
                v-model:sort-dir="sortDir"
                :items="sortItems"
                @update:sort-by="loadItems"
                @update:sort-dir="loadItems"
            />
        </v-col>
    </v-row>
    <v-row>
        <v-col
            cols="12"
            class="py-0"
        >
            <v-data-table
                :headers="headers"
                :items="props.items"
                :items-per-page="0"
                disable-sort
                hide-default-footer
                no-data-text="No results found."
            >
                <template #no-data>
                    <v-sheet class="mt-10">
                        <v-row
                            class="fill-height"
                            align="center"
                            justify="center"
                        >
                            <v-col
                                cols="12"
                                class="text-center"
                            >
                                <v-avatar size="128">
                                    <v-icon
                                        size="128"
                                        color="grey-lighten-2"
                                    >
                                        mdi-emoticon-sad-outline
                                    </v-icon>
                                </v-avatar>
                                <h2 class="text-h5 font-weight-bold">
                                    No members found.
                                </h2>
                                <p>
                                    Try adjusting your search criteria.
                                </p>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </template>
                <template #[`item.name`]="{ item }">
                    <v-list-item class="px-0">
                        <template #prepend>
                            <v-avatar
                                :image="`https://ui-avatars.com/api/?name=${item.name}`"
                            />
                        </template>
                        <v-list-item-title
                            class="text-body-2 font-weight-bold"
                        >
                            {{ item.name }}
                            <me-chip v-if="item.id === user.id" />
                        </v-list-item-title>
                        <v-list-item-subtitle
                            class="text-body-2 text-medium-emphasis"
                        >
                            {{ item.email }}
                        </v-list-item-subtitle>
                    </v-list-item>
                </template>
                <template #[`item.role`]="{ item }">
                    <role-select
                        v-if="isAdmin && item.id !== user.id && props.capacityFramework.user_id !== item.id"
                        :value="item.role"
                        @update:model-value="updateUserRole(item.id, $event)"
                    />
                    <span
                        v-else
                        class="ml-1 text-body-2 text-capitalize"
                    >
                        {{ item.role }}
                    </span>
                </template>
                <template #[`item.activity`]="{ item }">
                    <icon-date-tooltip
                        icon="mdi-account-plus-outline"
                        :date="item.user_created_at"
                    >
                        User created
                    </icon-date-tooltip>
                    <icon-date-tooltip
                        v-if="item.joined_at"
                        icon="mdi-check"
                        :date="item.joined_at"
                    >
                        Access granted
                    </icon-date-tooltip>
                </template>
                <template #[`item.actions`]="{ item }">
                    <maintainer-actions-btn
                        :capacity-framework="props.capacityFramework"
                        :maintainer="item"
                        @remove="removeMaintainer(item.id)"
                    />
                </template>
            </v-data-table>
        </v-col>
    </v-row>
</template>

<style scoped>

</style>
